import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/components/ui/use-toast";
import { Pencil, Trash2, Star, StarOff, Plus, X, ArrowLeft } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { AVAILABLE_SIZES } from "@/lib/constants";
import { useColorSettings } from "@/contexts/ColorSettingsContext";
import { ColorBadge } from "@/components/ColorBadge";

// Función para optimizar la imagen
const optimizeImage = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (event) => {
      const img = new Image();
      img.src = event.target.result;
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let width = img.width;
        let height = img.height;
        
        // Calcular nuevas dimensiones manteniendo la proporción
        const MAX_WIDTH = 800;
        const MAX_HEIGHT = 800;
        
        if (width > height) {
          if (width > MAX_WIDTH) {
            height = Math.round((height * MAX_WIDTH) / width);
            width = MAX_WIDTH;
          }
        } else {
          if (height > MAX_HEIGHT) {
            width = Math.round((width * MAX_HEIGHT) / height);
            height = MAX_HEIGHT;
          }
        }
        
        canvas.width = width;
        canvas.height = height;
        
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        
        // Comprimir la imagen con calidad ajustable
        let quality = 0.7;
        let dataUrl = canvas.toDataURL('image/jpeg', quality);
        
        // Reducir la calidad hasta que el tamaño sea menor a 50KB
        while (dataUrl.length > 50 * 1024 && quality > 0.1) {
          quality -= 0.1;
          dataUrl = canvas.toDataURL('image/jpeg', quality);
        }
        
        resolve(dataUrl);
      };
      img.onerror = reject;
    };
    reader.onerror = reject;
  });
};

export function ProductForm({ onClose, product = null }) {
  const [formData, setFormData] = useState({
    code: "",
    description: "",
    price: "",
    sizes: [],
    colors: [],
    images: [],
    mainImageIndex: 0,
    active: true,
    temporada: "",
    composicion: "",
    venta_solo_stock: false
  });
  const [allTags, setAllTags] = useState([]);
  const [selectedTagIds, setSelectedTagIds] = useState([]);
  const [newTag, setNewTag] = useState("");
  const [loading, setLoading] = useState(false);
  const { colorSettings, getActiveColors } = useColorSettings();
  const [inventory, setInventory] = useState([]);
  const [colorSearchTerm, setColorSearchTerm] = useState("");

  useEffect(() => {
    if (product) {
      setFormData({
        code: product.code || "",
        description: product.description || "",
        price: product.price || "",
        sizes: product.sizes || [],
        colors: product.colors || [],
        images: product.images || [],
        mainImageIndex: product.main_image_index || 0,
        active: product.active ?? true,
        temporada: product.temporada || "",
        composicion: product.composicion || "",
        venta_solo_stock: product.venta_solo_stock ?? false
      });

      const fetchTags = async () => {
        const { data: tagLinks } = await supabase
          .from('product_tags')
          .select('tag_id')
          .eq('product_id', product.id);
        setSelectedTagIds(tagLinks.map(t => t.tag_id));
      };
      fetchTags();

      const fetchInventory = async () => {
        const { data: inventoryData } = await supabase
          .from('inventory')
          .select('*')
          .eq('product_id', product.id);
        setInventory(inventoryData || []);
      };
      fetchInventory();
    }

    const fetchAllTags = async () => {
      const { data } = await supabase.from('tags').select('*').order('name');
      setAllTags(data || []);
    };
    fetchAllTags();
  }, [product]);

  // Forzar recarga de colores cuando se monte el componente
  useEffect(() => {
    // Forzar actualización de colores desde el contexto
    if (colorSettings && Object.keys(colorSettings).length > 0) {
      console.log('Colores cargados:', colorSettings);
    }
  }, [colorSettings]);

  const { toast } = useToast();

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setLoading(true);

      if (formData.images.length === 0) {
        toast({
          title: "Error",
          description: "Debe agregar al menos una imagen",
          variant: "destructive"
        });
        return;
      }

      // Si estamos editando, verificar si hay pedidos pendientes
      // if (product) {
      //   const { data: orderItems } = await supabase
      //     .from('order_items')
      //     .select('*')
      //     .eq('product_id', product.id)
      //     .eq('status', 'pending');

      //   if (orderItems?.length > 0 && !formData.active) {
      //     toast({
      //       title: "Error",
      //       description: "No se puede desactivar un producto con pedidos pendientes",
      //       variant: "destructive"
      //     });
      //     return;
      //   }
      // }

      // Guardar en Supabase
      const productData = {
        code: formData.code,
        description: formData.description,
        price: parseFloat(formData.price),
        sizes: formData.sizes,
        colors: formData.colors,
        images: formData.images,
        main_image_index: formData.mainImageIndex,
        active: formData.active,
        temporada: formData.temporada,
        composicion: formData.composicion,
        venta_solo_stock: formData.venta_solo_stock
      };

      let result;
      if (product) {
        result = await supabase
          .from('products')
          .update(productData)
          .eq('id', product.id)
          .select()
          .single();
      } else {
        result = await supabase
          .from('products')
          .insert(productData)
          .select()
          .single();
      }

      if (result.error) throw result.error;

      const productId = result.data.id;

      if (productId) {
        await supabase.from('product_tags').delete().eq('product_id', productId);
        const relations = selectedTagIds.map(tagId => ({
          product_id: productId,
          tag_id: tagId
        }));
        if (relations.length > 0) {
          await supabase.from('product_tags').insert(relations);
        }
      }

      toast({
        title: product ? "Producto actualizado" : "Producto creado",
        description: "Operación realizada con éxito"
      });

      onClose();
    } catch (error) {
      console.error('Error:', error);
      toast({
        title: "Error",
        description: "Ocurrió un error al guardar el producto",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  // Permite crear etiquetas dinámicamente y seleccionarlas
  const handleCreateTag = async () => {
    const trimmed = newTag.trim();
    if (!trimmed) return;

    const existing = allTags.find(t => t.name.toLowerCase() === trimmed.toLowerCase());
    if (existing) {
      if (!selectedTagIds.includes(existing.id)) {
        setSelectedTagIds(prev => [...prev, existing.id]);
      }
      setNewTag("");
      return;
    }

    const { data, error } = await supabase
      .from('tags')
      .insert({ name: trimmed })
      .select()
      .single();

    if (error) {
      toast({
        title: "Error",
        description: "No se pudo crear la etiqueta",
        variant: "destructive"
      });
      return;
    }

    setAllTags(prev => [...prev, data]);
    setSelectedTagIds(prev => [...prev, data.id]);
    setNewTag("");
  };

  const handleSizeToggle = (size) => {
    setFormData(prev => {
      const newSizes = prev.sizes.includes(size)
        ? prev.sizes.filter(s => s !== size)
        : [...prev.sizes, size];
      
      // Ordenar las tallas según AVAILABLE_SIZES
      return {
        ...prev,
        sizes: AVAILABLE_SIZES.filter(s => newSizes.includes(s))
      };
    });
  };

  const handleColorToggle = (color) => {
    setFormData(prev => ({
      ...prev,
      colors: prev.colors.includes(color)
        ? prev.colors.filter(c => c !== color)
        : [...prev.colors, color]
    }));
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      const optimizedImage = await optimizeImage(file);
      setFormData(prev => ({
        ...prev,
        images: [...prev.images, optimizedImage]
      }));
    } catch (error) {
      console.error('Error optimizing image:', error);
      toast({
        title: "Error",
        description: "No se pudo procesar la imagen",
        variant: "destructive"
      });
    }
  };

  const handleRemoveImage = (index) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index),
      mainImageIndex: prev.mainImageIndex >= index ? Math.max(0, prev.mainImageIndex - 1) : prev.mainImageIndex
    }));
  };

  const handleSetMainImage = (index) => {
    setFormData(prev => ({
      ...prev,
      mainImageIndex: index
    }));
  };

  return (
    <form onSubmit={handleSubmit} className="bg-white p-6 rounded-lg shadow-sm mb-6">
      {/* Botón de volver */}
      <div className="mb-6">
        <Button
          type="button"
          variant="ghost"
          onClick={onClose}
          className="flex items-center gap-2 text-blue-600 hover:text-blue-700 hover:bg-blue-50 border border-blue-200 hover:border-blue-300 shadow-sm hover:shadow-md transition-all duration-200 group px-4 py-2 rounded-lg"
        >
          <ArrowLeft className="h-5 w-5 group-hover:-translate-x-1 transition-transform duration-200 text-blue-500" />
          <span className="font-medium">Volver a Productos</span>
        </Button>
      </div>
      
      <div className="grid grid-cols-2 gap-4 mb-4">
        <div>
          <label className="block text-sm font-medium mb-1">Código</label>
          <Input
            required
            value={formData.code}
            onChange={(e) => setFormData(prev => ({ ...prev, code: e.target.value }))}
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Precio</label>
          <Input
            type="number"
            step="0.01"
            required
            value={formData.price}
            onChange={(e) => setFormData(prev => ({ ...prev, price: e.target.value }))}
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Temporada</label>
          <Input
            maxLength={10}
            value={formData.temporada}
            onChange={(e) => setFormData(prev => ({ ...prev, temporada: e.target.value }))}
            placeholder="Máximo 10 caracteres"
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Composición</label>
          <textarea
            className="w-full min-h-[100px] p-2 border rounded-md"
            value={formData.composicion}
            onChange={(e) => setFormData(prev => ({ ...prev, composicion: e.target.value }))}
            placeholder="Máximo 5 líneas de 30 caracteres"
            maxLength={150}
          />
        </div>
        <div className="col-span-2">
          <label className="block text-sm font-medium mb-1">Descripción</label>
          <Input
            required
            value={formData.description}
            onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
          />
        </div>
      </div>

      <div className="mb-4">
        <label className="block text-sm font-medium mb-2">Etiquetas</label>
        <div className="flex flex-wrap gap-2 mb-2">
          {selectedTagIds.map(tagId => {
            const tag = allTags.find(t => t.id === tagId);
            if (!tag) return null;
            return (
              <div
                key={tag.id}
                className="flex items-center bg-gray-200 text-sm rounded-full px-3 py-1"
              >
                <span className="mr-2">{tag.name}</span>
                <button
                  type="button"
                  onClick={() =>
                    setSelectedTagIds(prev => prev.filter(id => id !== tag.id))
                  }
                  className="text-gray-600 hover:text-red-600 font-bold focus:outline-none"
                >
                  ×
                </button>
              </div>
            );
          })}
        </div>
        <div className="flex gap-2">
          <Input
            placeholder="Nueva etiqueta..."
            value={newTag}
            onChange={(e) => setNewTag(e.target.value)}
          />
          <Button
            type="button"
            onClick={handleCreateTag}
            disabled={!newTag.trim()}
          >
            Agregar
          </Button>
        </div>
        {allTags.filter(tag => !selectedTagIds.includes(tag.id)).length > 0 && (
          <div className="mt-3">
            <label className="block text-sm font-medium mb-1">Seleccionar etiqueta existente</label>
            <div className="flex flex-wrap gap-2">
              {allTags
                .filter(tag => !selectedTagIds.includes(tag.id))
                .map(tag => (
                  <Button
                    key={tag.id}
                    type="button"
                    variant="outline"
                    onClick={() => setSelectedTagIds(prev => [...prev, tag.id])}
                  >
                    {tag.name}
                  </Button>
                ))}
            </div>
          </div>
        )}
      </div>

      <div className="mb-4">
        <label className="block text-sm font-medium mb-2">Tallas</label>
        <div className="flex flex-wrap gap-2">
          {AVAILABLE_SIZES.map((size) => (
            <Button
              key={size}
              type="button"
              variant={formData.sizes.includes(size) ? "default" : "outline"}
              onClick={() => handleSizeToggle(size)}
            >
              {size}
            </Button>
          ))}
        </div>
      </div>

      <div className="mb-4">
        <label className="block text-sm font-medium mb-2">Colores (Selección múltiple)</label>
        
        {/* Barra de búsqueda de colores */}
        <div className="mb-3">
          <Input
            type="text"
            placeholder="Buscar colores..."
            value={colorSearchTerm}
            onChange={(e) => setColorSearchTerm(e.target.value)}
            className="max-w-xs"
          />
        </div>

        {/* Contador de colores seleccionados */}
        <div className="mb-3 text-sm text-gray-600">
          {formData.colors.length > 0 ? (
            <span className="text-blue-600 font-medium">
              {formData.colors.length} color{formData.colors.length !== 1 ? 'es' : ''} seleccionado{formData.colors.length !== 1 ? 's' : ''}
            </span>
          ) : (
            <span className="text-gray-500">Ningún color seleccionado</span>
          )}
        </div>

        {/* Grid de colores disponibles */}
        <div className="grid grid-cols-10 gap-2 max-h-64 overflow-y-auto p-2 border rounded-lg bg-gray-50">
          {Object.entries(colorSettings)
            .filter(([colorId, colorData]) => 
              colorData.active !== false && // Solo colores activos
              (colorSearchTerm === "" || 
               colorId.toLowerCase().includes(colorSearchTerm.toLowerCase()) ||
               (colorData.description && colorData.description.toLowerCase().includes(colorSearchTerm.toLowerCase())))
            )
            .map(([colorId, colorData]) => {
              const selected = formData.colors.includes(colorId);
              return (
                <div
                  key={colorId}
                  onClick={() => handleColorToggle(colorId)}
                  className={`cursor-pointer flex flex-col items-center group p-1 rounded-lg transition-all duration-200 ${
                    selected 
                      ? 'ring-2 ring-blue-500 bg-blue-50 shadow-md scale-105' 
                      : 'ring-1 ring-gray-300 hover:ring-2 hover:ring-gray-400 hover:bg-white hover:shadow-sm'
                  }`}
                  title={`${colorId}: ${colorData.description || colorId}`}
                >
                  <ColorBadge color={colorId} />
                </div>
              );
            })}
        </div>

        {/* Mensaje si no hay colores disponibles */}
        {Object.keys(colorSettings).filter(key => colorSettings[key].active !== false).length === 0 && (
          <div className="text-center py-4 text-gray-500">
            No hay colores disponibles. Ve a Configuración → Gestión de Colores para agregar colores.
          </div>
        )}

        {/* Mensaje si la búsqueda no encuentra resultados */}
        {Object.keys(colorSettings).filter(key => colorSettings[key].active !== false).length > 0 && 
         Object.entries(colorSettings).filter(([colorId, colorData]) => 
           colorData.active !== false &&
           (colorSearchTerm === "" || 
            colorId.toLowerCase().includes(colorSearchTerm.toLowerCase()) ||
            (colorData.description && colorData.description.toLowerCase().includes(colorSearchTerm.toLowerCase())))
         ).length === 0 && (
          <div className="text-center py-2 text-gray-500 text-sm">
            No se encontraron colores que coincidan con "{colorSearchTerm}"
          </div>
        )}
      </div>

      <div className="mb-4">
        <label className="block text-sm font-medium mb-2">Imágenes</label>
        <div className="grid grid-cols-4 gap-4">
          {formData.images.map((image, index) => (
            <div key={index} className="relative group">
              <img
                src={image}
                alt={`Imagen ${index + 1}`}
                className="w-full h-32 object-cover rounded"
              />
              <div className="absolute inset-0 bg-black bg-opacity-50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => handleSetMainImage(index)}
                >
                  {index === formData.mainImageIndex ? (
                    <Star className="h-4 w-4 text-yellow-400" />
                  ) : (
                    <StarOff className="h-4 w-4 text-white" />
                  )}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => handleRemoveImage(index)}
                >
                  <Trash2 className="h-4 w-4 text-white" />
                </Button>
              </div>
            </div>
          ))}
          <div className="border-2 border-dashed rounded p-4 flex items-center justify-center">
            <input
              type="file"
              accept="image/*"
              onChange={handleImageUpload}
              className="hidden"
              id="image-upload"
            />
            <label
              htmlFor="image-upload"
              className="cursor-pointer text-sm text-gray-600 text-center"
            >
              <Plus className="h-6 w-6 mx-auto mb-2" />
              Agregar imagen
            </label>
          </div>
        </div>
      </div>

      <div className="mb-4">
        <label className="block text-sm font-medium mb-2">Inventario</label>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ width: '200px' }}>Color</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Stock por Talla</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ width: '120px' }}>Pedidos Pendientes</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {Object.entries(
                inventory.reduce((acc, item) => {
                  if (!acc[item.color]) {
                    acc[item.color] = {
                      sizes: {},
                      pending_orders: 0
                    };
                  }
                  acc[item.color].sizes[item.size] = item.quantity;
                  acc[item.color].pending_orders += item.pending_orders || 0;
                  return acc;
                }, {})
              ).map(([color, data]) => (
                <tr key={color}>
                  <td className="px-6 py-4" style={{ width: '200px' }}>
                    <div className="flex items-center">
                      <div className="flex-shrink-0 w-12 flex justify-center">
                        <ColorBadge color={color} />
                      </div>
                      <div className="ml-2 min-w-0 flex-1">
                        <div className="text-sm text-gray-900 truncate" title={color}>
                          {color}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-6">
                      {formData.sizes.map(size => (
                        <div key={size} className="flex items-center gap-1">
                          <span className="text-sm font-medium w-6">{size}:</span>
                          <span className={`text-sm ${data.sizes[size] > 0 ? 'text-green-600' : 'text-red-600'} w-6 text-right`}>
                            {data.sizes[size] || 0}
                          </span>
                        </div>
                      ))}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900" style={{ width: '120px' }}>
                    {data.pending_orders}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="mb-6">
        <div className="space-y-4">
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={formData.active}
              onChange={(e) => setFormData(prev => ({ ...prev, active: e.target.checked }))}
              className="mr-2"
            />
            <span className="text-sm font-medium">Producto activo</span>
          </label>
          
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={formData.venta_solo_stock}
              onChange={(e) => setFormData(prev => ({ ...prev, venta_solo_stock: e.target.checked }))}
              className="mr-2"
            />
            <span className="text-sm font-medium">Venta solo con stock disponible</span>
          </label>
        </div>
      </div>

      <div className="flex justify-end gap-2">
        <Button 
          type="button" 
          variant="outline" 
          onClick={onClose}
          disabled={loading}
        >
          Cancelar
        </Button>
        <Button 
          type="submit"
          disabled={loading}
        >
          {loading ? "Guardando..." : (product ? "Actualizar" : "Guardar")}
        </Button>
      </div>
    </form>
  );
}
